<template>
  <div>
    <div class="filter-row mb-3">
      <div class="filter-item filter-item--date">
        <span class="d-block text-muted small text-left font-weight-bold mb-2">Select Range</span>
        <v-date-picker
          v-model="filters.dateRange"
          is-range
          mode="date"
          :masks="masks"
          :is-dark="$store.getters['theme/isDark']">
          <template #default="{ inputValue, inputEvents, isDragging }">
            <input
              class="form-control bg-light border rounded"
              :class="isDragging ? 'text-muted' : 'text-dark'"
              :value="inputValue.start && inputValue.end ? inputValue.start + ' - ' + inputValue.end : 'no range selected'"
              v-on="inputEvents.start"
            />
          </template>
        </v-date-picker>
      </div>

      <div class="filter-item">
        <span class="d-block text-muted small text-left font-weight-bold mb-2">Range Type</span>
        <select name="" class="form-control" v-model="filters.rangeType">
          <option value="lastMonth">Last Month</option>
          <option value="thisMonth">This Month</option>
          <option value="lastWeek">Last Week</option>
          <option value="thisWeek">This Week</option>
          <option value="yesterday">Yesterday</option>
          <option value="today">Today</option>
          <option value="custom">Custom</option>
        </select>
      </div>

      <div class="filter-item">
        <span class="d-block text-muted small text-left font-weight-bold mb-2">Asset</span>
        <select name="" class="form-control" v-model="filters.selectedAsset">
          <option value="all" selected>All</option>
          <option v-for="asset in assets" :key="asset.massId" :value="asset.massId">{{ asset.massName }}</option>
        </select>
      </div>

      <div class="filter-item">
        <span class="d-block text-muted small text-left font-weight-bold mb-2">Order (By Bunker Date)</span>
        <div class="btn-group w-100" role="group">
          <button type="button" class="btn btn-outline-primary" :class="{'active' : filters.order === 'ASC'}" @click="filters.order = 'ASC'">Ascending</button>
          <button type="button" class="btn btn-outline-primary" :class="{'active' : filters.order === 'DESC'}" @click="filters.order = 'DESC'">Descending</button>
        </div>
      </div>

      <div class="filter-item filter-item--sm">
        <span class="d-block text-muted small text-left font-weight-bold mb-2">Unit</span>
        <div class="btn-group w-100" role="group">
          <button type="button" class="btn btn-outline-primary" :class="{'active' : filters.bunkerUnit === 'kl'}" @click="filters.bunkerUnit = 'kl'">KL</button>
          <button type="button" class="btn btn-outline-primary" :class="{'active' : filters.bunkerUnit === 'l'}" @click="filters.bunkerUnit = 'l'">L</button>
        </div>
      </div>

      <div class="filter-item filter-item--action">
        <span class="d-block text-muted small font-weight-bold mb-2 d-none d-md-block">&nbsp;</span>
        <div class="d-flex" style="gap: 8px;">
          <button class="btn btn-primary flex-grow-1" @click="applyFilters">Apply</button>
          <button class="btn btn-info flex-grow-1" @click="resetFilters">Reset</button>
        </div>
      </div>
    </div>
    <div v-if="isDirty" class="text-warning mt-2 mx-2">
      Filters have changed. Click Apply to update the data.
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'BunkeringLogFilter',
  props: {
    assets: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      filters: {
        dateRange: {
          start: moment().subtract(1, 'year').startOf('month').toDate(),
          end: moment().endOf('day').toDate(),
        },
        rangeType: 'thisMonth',
        selectedAsset: 'all',
        order: 'DESC',
        bunkerUnit: 'kl',
      },
      appliedSnapshot: null,
      masks: {
        input: 'DD-MM-YYYY',
      },
    };
  },
  computed: {
    isDirty() {
      if (!this.appliedSnapshot) return false;
      const current = this.getSnapshot();
      const applied = this.appliedSnapshot;
      return (
        current.start !== applied.start
        || current.end !== applied.end
        || current.rangeType !== applied.rangeType
        || current.selectedAsset !== applied.selectedAsset
        || current.order !== applied.order
        || current.bunkerUnit !== applied.bunkerUnit
      );
    },
  },
  methods: {
    applyFilters() {
      this.$emit('apply-filters', this.filters);
      this.appliedSnapshot = this.getSnapshot();
    },
    resetFilters() {
      this.filters = {
        dateRange: {
          start: moment().subtract(1, 'year').startOf('month').toDate(),
          end: moment().endOf('day').toDate(),
        },
        rangeType: 'thisMonth',
        selectedAsset: 'all',
        order: 'DESC',
        bunkerUnit: 'kl',
      };
      this.$emit('apply-filters', this.filters);
      this.appliedSnapshot = this.getSnapshot();
      this.$emit('reset-filters');
    },
    getSnapshot() {
      return {
        start: this.filters.dateRange?.start?.valueOf?.() ?? null,
        end: this.filters.dateRange?.end?.valueOf?.() ?? null,
        rangeType: this.filters.rangeType,
        selectedAsset: this.filters.selectedAsset,
        order: this.filters.order,
        bunkerUnit: this.filters.bunkerUnit,
      };
    },
  },
  mounted() {
    this.appliedSnapshot = this.getSnapshot();
  },
  watch: {
    'filters.rangeType': (newVal) => {
      switch (newVal) {
        case 'today':
          this.filters.dateRange = { start: moment().startOf('day').toDate(), end: moment().toDate() };
          break;
        case 'yesterday':
          this.filters.dateRange = { start: moment().subtract(1, 'day').startOf('day').toDate(), end: moment().subtract(1, 'day').endOf('day').toDate() };
          break;
        case 'thisWeek':
          this.filters.dateRange = { start: moment().startOf('week').toDate(), end: moment().toDate() };
          break;
        case 'lastWeek':
          this.filters.dateRange = { start: moment().subtract(1, 'week').startOf('week').toDate(), end: moment().subtract(1, 'week').endOf('week').toDate() };
          break;
        case 'thisMonth':
          this.filters.dateRange = { start: moment().startOf('month').toDate(), end: moment().toDate() };
          break;
        case 'lastMonth':
          this.filters.dateRange = { start: moment().subtract(1, 'month').startOf('month').toDate(), end: moment().subtract(1, 'month').endOf('month').toDate() };
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style scoped>
.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  padding-bottom: 8px;
}

@media (max-width: 575px) {
  .filter-row {
    gap: 16px;
  }
}

.filter-item {
  flex: 1;
  min-width: 150px;
}

.filter-item--date {
  min-width: 200px;
}

.filter-item--sm {
  flex: 0 0 auto;
  min-width: 100px;
}

.filter-item--action {
  flex: 0 0 auto;
}

@media (max-width: 575px) {
  .filter-item,
  .filter-item--date,
  .filter-item--sm,
  .filter-item--action {
    flex: 0 0 100%;
    min-width: 0;
  }
}
</style>
